import {
  Box,
  Button, 
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  LinearProgress,
  Paper,
  Theme,
  Tooltip,
  Typography
} from "@mui/material";
import { makeStyles } from "helpers/StyleHelper";
import { useTheme } from "@mui/material/styles";
import FindInPageIcon from "@mui/icons-material/FindInPage";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { PreviewPdfRequest } from "services/KnowfullyAdminClient";
import { Orientation } from "types/common-types";

export enum CertificateTemplateType {
  Default,
  Completion,
  Override,
}

export type CertificatePreviewBoxProps = {
  name: string;
  type: CertificateTemplateType;
  orientation: Orientation;
  html: string | undefined;
};

const useStyles = makeStyles((theme: Theme) =>
  ({
    paperWrapper: {
      width: "100%",
      overflow: "auto",
      backgroundColor: theme.palette.primary.light,
    },
    paper: {
      width: "100%",
      height: "100%",
    },
    professionLabel: {
      paddingTop: "10px",
      fontWeight: "bold",
      color: theme.palette.primary.dark,
      textDecoration: "underline",
    },
    descriptionBox: {
      width: "90%",
      padding: "8px",
    },
    actions: {
      width: "10%",
      display: "inline-block",
      verticalAlign: "top",
      textAlign: "right",
    },
    previewButton: {
      "&:hover": {
        color: "orange",
      },
    },
  })
);

const CertificatePreviewBox = (
  certificateTemplate: CertificatePreviewBoxProps
) => {
  const [createOpen, createOpenActions] = useBoolean(false);
  const { formatMessage: t } = useIntl();
  const styles = useStyles(useTheme());
  const { pdfService } = useGlobalContext();
  const [file, setFile] = useState<string | null>(null);

  useEffect(() => {
    async function previewPdf() {
      if (createOpen) {
        const response = await pdfService.preview(
          new PreviewPdfRequest({
            html: certificateTemplate.html,
            isLandscape:
              certificateTemplate.orientation === Orientation.Landscape,
          })
        );

        const fileData = new Blob([response.data], { type: "application/pdf" });
        const fileURL = URL.createObjectURL(fileData);

        setFile(fileURL);
      }
    }

    previewPdf();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createOpen]);

  return (
    <>
      <Paper sx={styles.paperWrapper}>
        <div style={{ display: "flex" }}>
          <Box sx={styles.descriptionBox}>
            <Typography
              variant="h6"
              style={{
                color:
                  certificateTemplate.type === CertificateTemplateType.Default
                    ? "darkorange"
                    : certificateTemplate.type ===
                      CertificateTemplateType.Completion
                    ? "darkgreen"
                    : "black",
                fontWeight:
                  certificateTemplate.type ===
                    CertificateTemplateType.Default ||
                  certificateTemplate.type ===
                    CertificateTemplateType.Completion
                    ? "bolder"
                    : "lighter",
              }}
            >
              {certificateTemplate.name}
            </Typography>
          </Box>
          <Tooltip
            title={
              certificateTemplate.html == null
                ? t({
                    id: "templateEmptyMessage",
                  })
                : ""
            }
          >
            <Box sx={styles.actions}>
              <Tooltip title={t({ id: "previewLabel" })}>
                <IconButton
                  color="primary"
                  component="span"
                  disabled={certificateTemplate.html == null}
                  sx={styles.previewButton}
                  onClick={() => createOpenActions.setTrue()}
                  data-cy={`${certificateTemplate.name}PreviewButton`}
                >
                  <FindInPageIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Tooltip>
        </div>
      </Paper>
      <br></br>
      <Dialog open={createOpen} disableEnforceFocus maxWidth="lg" fullWidth>
        <DialogContent style={{ height: "100vh" }}>
          <h2
            style={{
              backgroundColor: "lightgrey",
              borderTop: "1px solid black",
              borderBottom: "1px solid black",
            }}
          >
            <span style={{ margin: "10px" }}>{certificateTemplate.name}</span>
          </h2>
          {file != null ? (
            <iframe
              title="preview"
              src={file}
              style={{ width: "100%", height: "90%" }}
            />
          ) : (
            <LinearProgress color="secondary" />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setFile(null);
              createOpenActions.setFalse();
            }}
            data-cy="cancelButton"
          >
            {t({ id: "closeButton" })}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CertificatePreviewBox;
